import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Check, X, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, fmtDateTime, TOKEN_KEY, titleCase } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, Spinner } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Select, Input } from "@/components/common/Field";
import { pendingCount, queuePhoto, uploadPhoto } from "@/lib/offlineQueue";

const PANELS = ["front", "rear", "left", "right", "roof", "interior", "engine", "underbody", "tyres", "dashboard", "other"];
export const photoUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/photos/${id}/file?auth=${localStorage.getItem(TOKEN_KEY)}`;

export function PhotoCapture({ vehicleId, issueId, onUploaded }) {
  const inputRef = useRef();
  const panel = "other", kind = "general";
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(0);
  useEffect(() => { pendingCount().then(setPending); }, []);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const payload = { file, vehicle_id: vehicleId, panel, kind, note: note || null, issue_id: issueId || null };
    if (!navigator.onLine) { await queuePhoto(payload); setPending(await pendingCount()); toast.info("Offline — photo queued and will upload when you reconnect"); return; }
    try { await uploadPhoto(payload); toast.success("Photo uploaded — awaiting review"); setNote(""); onUploaded?.(); } catch (err) {
      if (!err.response) { await queuePhoto(payload); setPending(await pendingCount()); toast.info("Network issue — photo queued"); } else toast.error(errorMessage(err));
    }
    e.target.value = "";
  };
  return (
    <div className="fv-card flex flex-wrap items-end gap-2 p-3" data-testid="photo-capture">
      <label className="min-w-[160px] flex-1 text-sm"><span className="fv-caption block">Note</span><Input data-testid="photo-note-input" className="h-9" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" /></label>
      <input ref={inputRef} data-testid="photo-file-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
      <button onClick={() => inputRef.current.click()} data-testid="photo-capture-btn" className="flex h-9 items-center gap-1 rounded-full bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-[#0071e3]"><Camera className="h-4 w-4" />Capture / upload</button>
      {pending > 0 && <span className="text-xs text-muted-foreground" data-testid="photo-queue-count">{pending} queued offline</span>}
    </div>
  );
}

export function PhotoGrid({ photos, reload, reviewMode }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canReview = can("admin", "fleet_manager", "garage_supervisor");
  const review = async (p, status) => { await api.post(`/photos/${p.id}/review`, { status }); toast.success(`Photo ${status}`); reload(); };
  const convert = async (p) => {
    const title = window.prompt("Issue title", `Damage on ${p.panel} panel`);
    if (!title) return;
    const { data } = await api.post(`/photos/${p.id}/convert-to-issue`, { title, category: "body", priority: "medium" });
    toast.success("Issue created from photo"); navigate(`/issues/${data.issue_id}`);
  };
  if (!photos) return <Spinner />;
  if (photos.length === 0) return <EmptyState testId="photos-empty" title="No photos" description={reviewMode ? "Nothing waiting for review." : "Capture the first photo of this vehicle."} />;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="photo-grid">
      {photos.map((p) => (
        <figure key={p.id} className="fv-card overflow-hidden" data-testid={`photo-card-${p.id}`}>
          <img src={photoUrl(p.id)} alt={`${p.panel} ${p.kind}`} className="aspect-[4/3] w-full object-cover" loading="lazy" />
          <figcaption className="space-y-1 p-2">
            <div className="flex items-center justify-between gap-1"><span className="text-xs font-semibold">{p.vehicle_name || p.registration_no}</span><StatusBadge status={p.review_status === "pending" ? "reported" : p.review_status === "approved" ? "resolved" : "expired"} label={titleCase(p.review_status)} testId={`photo-review-${p.id}`} /></div>
            <p className="truncate text-[11px] text-muted-foreground">{p.taken_by_name} · {fmtDateTime(p.created_at)}{p.note ? ` · ${p.note}` : ""}</p>
            {canReview && (
              <div className="flex gap-1 pt-1">
                {p.review_status === "pending" && <><button onClick={() => review(p, "approved")} data-testid={`approve-photo-${p.id}`} className="flex h-7 flex-1 items-center justify-center gap-1 rounded-md bg-primary/10 text-xs font-medium text-primary"><Check className="h-3 w-3" />Approve</button><button onClick={() => review(p, "rejected")} data-testid={`reject-photo-${p.id}`} className="flex h-7 flex-1 items-center justify-center gap-1 rounded-md bg-secondary text-xs font-medium"><X className="h-3 w-3" />Reject</button></>}
                {!p.issue_id && <button onClick={() => convert(p)} data-testid={`convert-photo-${p.id}`} className="flex h-7 flex-1 items-center justify-center gap-1 rounded-md bg-secondary text-xs font-medium"><Wrench className="h-3 w-3" />To issue</button>}
                {p.issue_id && <button onClick={() => navigate(`/issues/${p.issue_id}`)} data-testid={`photo-issue-link-${p.id}`} className="h-7 flex-1 rounded-md bg-secondary text-xs font-medium">View issue</button>}
              </div>
            )}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

export function PhotosTab({ vehicleId }) {
  const [photos, setPhotos] = useState(null);
  const load = useCallback(() => api.get("/photos", { params: { vehicle_id: vehicleId } }).then((r) => setPhotos(r.data)), [vehicleId]);
  useEffect(() => { load(); }, [load]);
  const before = photos?.filter((p) => p.kind === "before") || [], after = photos?.filter((p) => p.kind === "after") || [];
  return (
    <div className="space-y-4">
      <PhotoCapture vehicleId={vehicleId} onUploaded={load} />
      {before.length > 0 && after.length > 0 && <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="before-after"><div><p className="fv-caption mb-1">Before</p><img src={photoUrl(before[0].id)} alt="before" className="fv-card aspect-[4/3] w-full object-cover" /></div><div><p className="fv-caption mb-1">After</p><img src={photoUrl(after[0].id)} alt="after" className="fv-card aspect-[4/3] w-full object-cover" /></div></div>}
      <PhotoGrid photos={photos} reload={load} />
    </div>
  );
}
